<template>
    <div :class="$style.templateContent">
        <div :class="[$style.tabs, isPopupType && $style.popup]">
            <pt-tabs v-model="activeTab" v-if="isPopupType">
                <pt-tab-pane
                    v-for="item in templateTabList"
                    :key="item.key"
                    :name="item.key"
                    :label="$t(item.name)"
                ></pt-tab-pane>
            </pt-tabs>
        </div>

        <TemplateListWithFilter
            :position="position"
            :entryPosition="props?.entryPosition"
            location="Create Popup Dialog"
            :engageType="props.data?.templateItem || props.entryItem"
            :isOwn="activeTab === 'OWN'"
            :activeTab="activeTab"
            :showBlank="true"
            :emptyTip="props.data?.templateItem?.key === 'ABTEST'"
            :webType="profileInfo?.webType"
            :webTarget="profileInfo?.webTarget"
            @view="hanleViewTemplate"
            :loading="false"
            @create="handleCreateTemplate"
        >
        </TemplateListWithFilter>
    </div>
    <Teleport to="body" v-if="detailDialogVisible">
        <ExperienceTemplateDetailDialog
            :position="props.entryItem.key.toLocaleLowerCase + '_list'"
            :visible="detailDialogVisible"
            :template="currentSelectTemplate"
            @view="handleCreateTemplate"
            @create="handleCreateTemplate"
            @close="detailDialogVisible = false"
        ></ExperienceTemplateDetailDialog>
    </Teleport>
</template>

<script lang="ts">
import TemplateFilter from '@experience/components/template/TemplateFilter.vue';
import TemplateRecommandList from '@experience/components/template/TemplateRecommandList.vue';
import TemplateListWithFilter from '@experience/components/template/TemplateListWithFilter.vue';
import ExperienceTemplateDetailDialog from '@experience/components/dialog/ExperienceTemplateDetailDialog.vue';

export default {
    name: 'ExperienceTemplate',
    components: {
        TemplateFilter,
        TemplateRecommandList,
        TemplateListWithFilter,
        ExperienceTemplateDetailDialog
    }
};
</script>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { templateTabList } from '@experience/static/config/experience.config';
import type { TypeItem } from '@experience/static/config/experience.type';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import type { ECTemplate } from '@models/ecshop/asset';
import analyticsService from '@assets/services/analytics.service';
import { ComponentData, EntryPosition } from '@experience/store/experience.create.store';
import { ECDataLocation } from '@experience/hooks/template.hooks';
import { useProfileInfo } from '@models/profile/useCurrentProfile';

// interface
interface Props {
    entryItem: TypeItem;
    entryPosition: EntryPosition;
    data: ComponentData;
}

// propss
const props = defineProps<Props>();

// emits
const emit = defineEmits(['close', 'back', 'view', 'update', 'createWithTemplate']);

// state
const { t } = useI18n();
const activeTab = ref<'OWN' | 'COMMON' | undefined>('COMMON');
const detailDialogVisible = ref(false);
const currentSelectTemplate = ref();
const { profileInfo } = useProfileInfo();

// computed
const position = computed(() => {
    const key = props.data?.templateItem?.key || props?.entryItem?.key;
    const mapping: { [key: string]: ECDataLocation } = {
        POPUP: 'popup_list',
        INLINE: 'inline_list',
        ABTEST: 'ab_list'
    };
    return mapping[key];
});

const isPopupType = computed(() => {
    return (props.data?.templateItem?.key || props.entryItem.key) === 'POPUP';
});

// function
function handleBack() {
    emit('back');
}

function hanleViewTemplate(template: ExperienceTemplateV4 | ECTemplate) {
    detailDialogVisible.value = true;
    currentSelectTemplate.value = template;
}

function handleCreateTemplate(template: ExperienceTemplateV4 | ECTemplate) {
    const isBlankTemplate = template.id === 'blank';
    analyticsService
        .usePTX('cp_add_click', {
            type: props.entryItem.trackKey,
            is_blank: isBlankTemplate ? 'Y' : 'N',
            template_position: isBlankTemplate
                ? ''
                : detailDialogVisible.value
                ? 'template_detail'
                : 'template_list',
            template_name: isBlankTemplate ? '' : template.name
        })
        .track();

    if (isBlankTemplate && ['POPUP', 'STICKY_BAR'].includes(template.type || '')) {
        emit('update', {
            dialog: {
                component: 'ExperienceTemplateBlank',
                componentData: {
                    visibleBack: false,
                    type: template.type?.toLocaleLowerCase()
                }
            }
        });
        analyticsService
            .usePTX('cp_template_create_click', {
                type: template.type,
                is_blank: 'Y',
                template_position: detailDialogVisible.value ? 'template_detail' : 'template_list',
                template_name: ''
            })
            .track();
        return;
    }

    emit('createWithTemplate', {
        template,
        templateSource: activeTab.value,
        backTemplateName: 'ExperienceTemplate'
    });
    detailDialogVisible.value = false;
}

function handleClose() {
    emit('close');
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.templateContent {
    padding: 0 46px;
}
.tabs {
    padding: 0 40px;
    border-bottom: 1px solid transparent;
    margin-bottom: 88px;
    &.popup {
        border-color: $pt-black-50;
    }
    :global {
        .pt-tabs__nav-wrap::after {
            display: none;
        }
        .pt-tabs__content {
            margin: 0;
        }
    }
}
</style>
